import React, { Fragment, useState, useEffect, useRef } from "react";
import {
  Grid,
  Typography,
  Button,
  Card,
  CardContent,
  Tabs,
  Tab,
  Box,
  Avatar,
  IconButton,
  TablePagination,
} from "@mui/material";
import AddTwoToneIcon from "@mui/icons-material/AddTwoTone";
import EditIcon from "@mui/icons-material/Edit";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import PropTypes from "prop-types";
import axios from "axios"; // Add axios or fetch API
import FeedbackModal from "../modals/addFeedbackModal";
import { AddFeedback } from "../interfaces/addFeedback";
import { createFeedback, getRmIds, getSendAndReceivedFeedback, updateFeedback } from "src/services/feedbackService";
import SnackBar from 'src/components/SnackBar/SnackBar';
import { useDispatch, useSelector } from "react-redux";
import { AppActions } from "src/store/AppReducer";
import { RootStateType } from "src/store/RootReducer";
import dayjs from "dayjs";
import { validateMultiRolesAndPermissions } from "src/utils/validate";
import { getRolePermissions } from "src/services/authService";
import { useNavigate } from "react-router";


// Reusable Star Rating Component
const StarRating = ({ value }) => {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    stars.push(
      i <= value ? (
        <StarIcon key={i} color="warning" fontSize="small" />
      ) : (
        <StarBorderIcon key={i} color="warning" fontSize="small" />
      )
    );
  }
  return <Box>{stars}</Box>;
};

StarRating.propTypes = {
  value: PropTypes.number.isRequired,
};

// Feedback Card Component
const FeedbackCard = ({ name, createdDate, receiver, sender, project, category, description, rating, editData, handleEditClick, isExpanded, status, tabValue, receiverDesignation, senderDesignation, appUser,
  onToggleExpand }) => {

  // const isEditable = appUser === sender && dayjs().diff(dayjs(createdDate), 'day') <= 7;
  const commentRef = useRef(null);
  const [showSeeMore, setShowSeeMore] = useState(false);

  useEffect(() => {
    if (commentRef.current) {
      const element = commentRef.current;
      if (isExpanded) {
        setShowSeeMore(true);
      } else {
        const isOverflowing = element.scrollHeight > element.clientHeight;
        setShowSeeMore(isOverflowing);
      }

    }
  }, [description, isExpanded]);

  return (
    <Card
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        background: "rgba(98, 103, 253, 0.05)",
      }}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <Avatar
              // src={`data:image/jpeg;base64,${tabValue === 0 ? senderImage : receiverImage}`}
              src={process.env.PUBLIC_URL + '/img/user.png'}
              alt={name}
              sx={{ width: 50, height: 50, borderRadius: 0 }}
            />
          </Grid>
          <Grid item xs>
            <Typography variant="subtitle1" fontWeight="bold">
              {tabValue === 0 ? sender : receiver}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {tabValue === 0 ? senderDesignation : receiverDesignation}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {createdDate}
            </Typography>
          </Grid>
          <Grid id="starSet" item>
            <StarRating value={rating} />
          </Grid>
        </Grid>
        <Box mt={1}>
          <Typography sx={{ paddingTop: '4px' }}>
            To which Project: <span style={{ fontWeight: 'bold' }}>{project}</span>
          </Typography>
          <Typography sx={{ paddingTop: '4px', paddingBottom: '4px' }}>
            Summary: <span style={{ fontWeight: 'bold' }}>{category}</span>
          </Typography>
          {/* <Typography
            sx={{

              overflow: 'hidden',
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: isExpanded ? 'none' : 2,
              paddingTop: '4px'
            }}
          >
            Comment: <span style={{ fontStyle: 'italic' }}> "{description}" </span>
          </Typography>
          {description?.length > 100 && (
            <Typography
              variant="body2"
              sx={{ color: 'blue', cursor: 'pointer', mt: 1 }}
              onClick={onToggleExpand}
            >
              {isExpanded ? "See less" : "See more"}
            </Typography>
          )} */}
          <Typography
            ref={commentRef}
            sx={{
              marginTop: '4px',
              overflow: "hidden",
              display: "-webkit-box",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: isExpanded ? "none" : 2,

            }}
          >
            Comment: <span style={{ fontStyle: "italic" }}> "{description}" </span>
          </Typography>
          {showSeeMore && (
            <Typography
              variant="body2"
              sx={{ color: "blue", cursor: "pointer", mt: 1 }}
              onClick={onToggleExpand}
            >
              {isExpanded ? "See less" : "See more"}
            </Typography>
          )}
        </Box>
      </CardContent>
      {tabValue !== 0 && status === "Y" && (
        <Box textAlign="right" p={1}>
          <IconButton onClick={() => handleEditClick(editData)} color="primary" size="small">
            <EditIcon fontSize="small" />
          </IconButton>
        </Box>
      )}
    </Card>
  );
}

FeedbackCard.propTypes = {
  name: PropTypes.string,
  date: PropTypes.string,
  project: PropTypes.string.isRequired,
  summary: PropTypes.string,
  incident: PropTypes.string,
  stars: PropTypes.number,
  editData: PropTypes.any,
  handleEditClick: PropTypes.func.isRequired,
};

// Main Feedback View
const FeedbackView = () => {
  const [tabValue, setTabValue] = useState(0);
  const [feedbackData, setFeedbackData] = useState([]); // To hold API data
  const [taskOpen, setTaskOpen] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [editData, setEditData] = useState<AddFeedback | null>(null);
  const [totalElements, setTotalElements] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(6);
  const dispatch = useDispatch();
  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState('');
  const [snackBarMessage, setSnackBarMessage] = useState('');
  const [expandedComments, setExpandedComments] = useState({});
  const appUser = useSelector((state: RootStateType) => state.appReducer?.user);
  const [permissions, setPermissions] = useState([]);
  const navigate = useNavigate();

  const handleEditClick = (data: AddFeedback) => {
    setEditData(data);
    setModalOpen(true);
  };


  const handleSave = async (data: AddFeedback) => {
    if (editData) {
      // Update existing feedback logic
      try {

        const bodydata = {
          "id": data.id,
          "receiverId": data.receiverId,
          "projectId": data.projectId,
          "categoryId": data.categoryId,
          "rating": data.rating,
          "description": data.description,
        }
        dispatch(AppActions.showLoader());

        const response = await updateFeedback(bodydata);
        dispatch(AppActions.hideLoader());
        setSnackBarMessage('Your Feedback has been submitted successfully.');
        setSnackbarSeverity('success');
        setSnackBarOpen(true);
        fetchFeedbackData(currentPage + 1, rowsPerPage, body)
      } catch (error) {
        setSnackBarMessage('Error creating feedback');
        setSnackbarSeverity('error');
        setSnackBarOpen(true);
        console.error('Error creating feedback:', error);
      }
    } else {
      // Add new feedback logic
    }
    setModalOpen(false);
  };


  const toggleTaskOpen = () => {
    setTaskOpen(!taskOpen);
  };


  const onSubmitToCreate = async (data: AddFeedback) => {

    try {
      dispatch(AppActions.showLoader());

      const response = await createFeedback(data);
      dispatch(AppActions.hideLoader());
      setSnackBarMessage('Your Feedback has been submitted successfully.');
      setSnackbarSeverity('success');
      setSnackBarOpen(true);
      setTaskOpen(!taskOpen);
    } catch (error) {
      setSnackBarMessage('Error creating feedback');
      setSnackbarSeverity('error');
      setSnackBarOpen(true);
      console.error('Error creating feedback:', error);
      dispatch(AppActions.hideLoader());
    }


  }



  const handleTabChange = (_, newValue) => {
    setTabValue(newValue);
    setCurrentPage(0);
  };

  const handlePageChange = (_, newPage) => {
    setCurrentPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(0);
  };


  const body = {
    senders: [],
    receivers: [],
  };

  useEffect(() => {
    fetchFeedbackData(currentPage + 1, rowsPerPage, body);
  }, [tabValue, currentPage, rowsPerPage]);

  const fetchFeedbackData = async (page, size, body) => {
    try {
      dispatch(AppActions.showLoader());
      const isSender = tabValue === 1;

      const response = await getSendAndReceivedFeedback(isSender, size, page, body);
      setFeedbackData(response.data.data.feedbackList);
      setTotalElements(response.data.data.totalElements);
      setCurrentPage(response.data.data.currentPage - 1);
      dispatch(AppActions.hideLoader());
    } catch (error) {
      console.error("Error fetching feedback data:", error);
      setSnackBarMessage('EError fetching feedback data');
      setSnackbarSeverity('error');
      setSnackBarOpen(true);
      dispatch(AppActions.hideLoader());
    }
  };

  const toggleExpandComment = (index) => {
    setExpandedComments((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };



  const getPermissions = (mail) => {
    getRolePermissions(mail).then((res) => {
      const tabs = validateMultiRolesAndPermissions(res);
      const arr = tabs.filter((fil) => fil.name === 'Feedback');
      setPermissions(arr[0].permissions);
    });
  };

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');

    if (Object.keys(user).length === 0) {
      navigate('/');
    } else {
      getPermissions(user.Email);
    }
  }, [navigate]);

  return (
    <Fragment>

      <Grid container sx={{ mt: 2, mb: 2 }} justifyContent="space-between">
        <Grid item>
          <Typography
            sx={{
              fontWeight: "700",
              fontSize: "16px",
              fontFamily: "Inter, Arial, sans-serif",
            }}
          >
            Feedbacks
          </Typography>
        </Grid>
        <Grid item>
          {permissions && (
            permissions.some((perm) => perm.name === 'Add Feedback' && perm.value === true) && (
              <Button
                variant="contained"
                startIcon={<AddTwoToneIcon fontSize="small" />}
                sx={{ height: "1.75rem", fontSize: "11px" }}
                onClick={() => {
                  setEditData(null);     // 🆕 Ensures you're in 'Add' mode
                  setTaskOpen(true);
                }}

              >
                Add Feedback
              </Button>))}

        </Grid>
      </Grid>

      <Card>

        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
        >
          {permissions.some((perm) => perm.name === 'Received' && perm.value === true) && <Tab label="Received" />}
          {permissions.some((perm) => perm.name === 'Sent' && perm.value === true) && <Tab label="Sent" />}

        </Tabs>



        <Box sx={{ p: 2 }}>
          <Grid container spacing={2}>
            {feedbackData.map((feedback, index) => (
              <Grid item xs={12} md={6} key={index}>
                <FeedbackCard
                  {...feedback}
                  tabValue={tabValue}
                  editData={feedback}
                  handleEditClick={handleEditClick}
                  isExpanded={expandedComments[index] || false}
                  appUser={appUser.Name}
                  onToggleExpand={() => toggleExpandComment(index)}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
        {feedbackData?.length === 0 && (
          <Typography
            style={{
              fontSize: '15px',
              fontWeight: 'bold',
              margin: '1rem',
              textAlign: 'center'
            }}
            variant="body2"
          >
            No Records Found
          </Typography>
        )}
        <Box p={2}>
          <TablePagination
            component="div"
            count={totalElements}
            page={currentPage}
            onPageChange={handlePageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[6, 12, 18, 24, 30]}
            labelRowsPerPage="Rows per page"
          />
        </Box>


      </Card>
      <FeedbackModal
        open={taskOpen}
        toggle={toggleTaskOpen}
        onSave={onSubmitToCreate}
      />

      <FeedbackModal
        open={modalOpen}
        toggle={() => setModalOpen(false)}
        onSave={handleSave}
        editData={editData}
        tabValue={tabValue}
      />
      <SnackBar
        snackBarOpen={snackBarOpen}
        setSnackBarOpen={setSnackBarOpen}
        snackBarMessage={snackBarMessage}
        snackbarSeverity={snackbarSeverity}
      />
    </Fragment>



  );
};

export default FeedbackView;